import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, FileSpreadsheet, Wallet, TrendingUp, TrendingDown, Coins, Tags, Target } from "lucide-react";
import api, { errMsg, downloadBlob } from "@/lib/api";
import { PageHeader, SectionCard, StatCard, Money, Loading, EmptyState, Pagination } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatTanggal, formatRupiah } from "@/lib/format";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStartStr = () => todayStr().slice(0, 8) + "01";
const DIR_LABEL = { in: "Pemasukan", out: "Pengeluaran" };
const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const monthLabel = (m) => { const [y, mo] = String(m).split("-"); return `${BULAN[Number(mo) - 1] || mo} ${String(y).slice(2)}`; };
const compactRp = (v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : `${v}`);
const trendLabel = (n) => (n === "income" ? "Pemasukan" : n === "expense" ? "Pengeluaran" : "Saldo");
const PIE_COLORS = ["#b91c1c", "#ea580c", "#d97706", "#ca8a04", "#65a30d", "#0891b2", "#2563eb", "#7c3aed", "#db2777", "#4b5563"];

export default function KeuanganOperasional() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());
  const [direction, setDirection] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);

  const params = {
    date_from: from || undefined, date_to: to || undefined,
    direction: direction !== "all" ? direction : undefined, q: q || undefined,
  };

  const { data: summary } = useQuery({
    queryKey: ["op-summary", from, to],
    queryFn: async () => (await api.get("/operational/summary", { params: { date_from: from || undefined, date_to: to || undefined } })).data,
  });
  const { data, isLoading } = useQuery({
    queryKey: ["op-entries", from, to, direction, q, page],
    queryFn: async () => (await api.get("/operational/entries", { params: { ...params, page, page_size: 20 } })).data,
  });
  const { data: trend = [] } = useQuery({
    queryKey: ["op-trend"],
    queryFn: async () => (await api.get("/operational/trend", { params: { months: 6 } })).data,
  });
  const { data: categories = [] } = useQuery({
    queryKey: ["op-categories"],
    queryFn: async () => (await api.get("/operational/categories")).data,
  });
  const { data: budgetData } = useQuery({
    queryKey: ["op-budgets"],
    queryFn: async () => (await api.get("/operational/budgets")).data,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["op-entries"] }); qc.invalidateQueries({ queryKey: ["op-summary"] }); qc.invalidateQueries({ queryKey: ["op-trend"] }); qc.invalidateQueries({ queryKey: ["op-categories"] }); qc.invalidateQueries({ queryKey: ["op-budgets"] }); };

  const del = async (e) => {
    if (!window.confirm(`Hapus entri "${e.description}"?`)) return;
    try { await api.delete(`/operational/entries/${e.id}`); toast.success("Entri dihapus."); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const exportLedger = async (fmt) => {
    try {
      const res = await api.get(`/operational/export.${fmt}`, { params, responseType: "blob" });
      downloadBlob(res.data, `keuangan-operasional.${fmt}`);
    } catch { toast.error("Gagal mengekspor."); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Keuangan Operasional"
        description="Buku kas manual pemasukan & pengeluaran operasional (khusus superadmin, terpisah dari pembayaran sewa)."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportLedger("csv")} data-testid="op-export-csv"><FileSpreadsheet className="mr-2 h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={() => exportLedger("xlsx")} data-testid="op-export-xlsx"><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel</Button>
          <Button variant="outline" onClick={() => setBudgetOpen(true)} data-testid="op-manage-budgets"><Target className="mr-2 h-4 w-4" /> Atur Anggaran</Button>
          <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="op-manage-categories"><Tags className="mr-2 h-4 w-4" /> Kelola Kategori</Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="op-add-button"><Plus className="mr-2 h-4 w-4" /> Tambah Entri</Button>
        </div>} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard testid="op-stat-income" label="Pemasukan" value={<Money value={summary?.income || 0} />} icon={TrendingUp} accent="#047857" />
        <StatCard testid="op-stat-expense" label="Pengeluaran" value={<Money value={summary?.expense || 0} />} icon={TrendingDown} accent="#b91c1c" />
        <StatCard testid="op-stat-net" label="Saldo Bersih" value={<Money value={summary?.net || 0} />} icon={Wallet} accent="#0369a1" />
      </div>

      {budgetData?.budgets?.length > 0 && (
        <SectionCard title={`Anggaran Bulanan (${budgetData.month})`} description="Pantau pengeluaran per kategori terhadap batas anggaran bulan ini." testid="op-budgets">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {budgetData.budgets.map((b) => {
              const color = b.status === "exceeded" ? "#b91c1c" : b.status === "warning" ? "#b45309" : "#047857";
              const note = b.status === "exceeded" ? " · Melebihi batas!" : b.status === "warning" ? " · Hampir batas" : "";
              return (
                <div key={b.id} data-testid={`op-budget-${b.id}`} className="rounded-lg border px-3 py-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{b.category}</span>
                    <span className="tabular-nums text-xs font-semibold" style={{ color }}>{b.percent}%{note}</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, b.percent)}%`, backgroundColor: color }} />
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                    <span><Money value={b.spent} /> terpakai</span>
                    <span>dari <Money value={b.limit} /></span>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {trend.some((t) => t.income || t.expense) && (
        <SectionCard title="Tren Pemasukan vs Pengeluaran" description="Perbandingan arus kas operasional 6 bulan terakhir." testid="op-trend">
          <div className="h-72 w-full" data-testid="op-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={compactRp} tick={{ fontSize: 12 }} width={48} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n) => [formatRupiah(v), trendLabel(n)]} labelFormatter={monthLabel} />
                <Legend formatter={trendLabel} />
                <Bar dataKey="income" fill="#047857" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" fill="#b91c1c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      )}

      {summary?.by_category?.some((c) => c.expense > 0) && (
        <SectionCard title="Rincian Pengeluaran per Kategori" description="Berdasarkan periode yang dipilih." testid="op-by-category">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="h-64" data-testid="op-category-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={summary.by_category.filter((c) => c.expense > 0).map((c) => ({ name: c.category, value: c.expense }))}
                       dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                    {summary.by_category.filter((c) => c.expense > 0).map((c, i) => <Cell key={c.category} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatRupiah(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1">
              {summary.by_category.filter((c) => c.expense > 0 || c.income > 0).map((c) => (
                <div key={c.category} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm" data-testid={`op-cat-row-${c.category}`}>
                  <span className="font-medium">{c.category}</span>
                  <span className="flex gap-3 tabular-nums">
                    {c.income > 0 && <span className="text-emerald-700">+<Money value={c.income} /></span>}
                    {c.expense > 0 && <span className="text-red-700">−<Money value={c.expense} /></span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      )}

      <SectionCard className="!p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} data-testid="op-filter-from" /></div>
          <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} data-testid="op-filter-to" /></div>
          <div>
            <Label className="text-xs">Jenis</Label>
            <Select value={direction} onValueChange={(v) => { setDirection(v); setPage(1); }}>
              <SelectTrigger data-testid="op-filter-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="in">Pemasukan</SelectItem>
                <SelectItem value="out">Pengeluaran</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Cari</Label><Input placeholder="Keterangan / kategori / referensi" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="op-filter-q" /></div>
        </div>
      </SectionCard>

      <SectionCard className="!p-0 overflow-hidden">
        {isLoading ? <Loading /> : !data?.items?.length ? (
          <EmptyState title="Belum ada entri" description="Tambahkan pemasukan atau pengeluaran operasional." icon={Coins} />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Jenis</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Keterangan</TableHead>
                  <TableHead className="text-right">Nominal</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((e) => (
                  <TableRow key={e.id} data-testid={`op-row-${e.id}`}>
                    <TableCell className="whitespace-nowrap">{formatTanggal(e.date)}</TableCell>
                    <TableCell>
                      <span className="rounded-full px-2 py-0.5 text-xs font-medium"
                        style={e.direction === "in" ? { color: "#047857", backgroundColor: "#ecfdf5" } : { color: "#b91c1c", backgroundColor: "#fef2f2" }}>
                        {DIR_LABEL[e.direction]}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{e.category}</TableCell>
                    <TableCell className="font-medium">{e.description}{e.reference ? <span className="text-xs text-muted-foreground"> · {e.reference}</span> : null}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold" style={{ color: e.direction === "in" ? "#047857" : "#b91c1c" }}>
                      {e.direction === "in" ? "+" : "−"}<Money value={e.amount} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditEntry(e)} data-testid={`op-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" onClick={() => del(e)} data-testid={`op-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-4"><Pagination page={page} total={data.total} pageSize={data.page_size} onPage={setPage} /></div>
          </>
        )}
      </SectionCard>

      {createOpen && <EntryDialog categories={categories} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); refresh(); }} />}
      {editEntry && <EntryDialog entry={editEntry} categories={categories} onClose={() => setEditEntry(null)} onDone={() => { setEditEntry(null); refresh(); }} />}
      {catOpen && <CategoryDialog categories={categories} onClose={() => setCatOpen(false)} onChanged={() => qc.invalidateQueries({ queryKey: ["op-categories"] })} />}
      {budgetOpen && <BudgetDialog budgets={budgetData?.budgets || []} categories={categories} onClose={() => setBudgetOpen(false)} onChanged={() => qc.invalidateQueries({ queryKey: ["op-budgets"] })} />}
    </div>
  );
}

function EntryDialog({ entry, categories = [], onClose, onDone }) {
  const isEdit = !!entry;
  const [f, setF] = useState({
    date: entry?.date || todayStr(), direction: entry?.direction || "out",
    category: entry?.category || "", description: entry?.description || "",
    amount: entry?.amount != null ? String(entry.amount) : "", method: entry?.method || "cash",
    reference: entry?.reference || "", note: entry?.note || "",
  });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.description.trim()) { toast.error("Keterangan wajib diisi."); return; }
    if (!Number(f.amount) || Number(f.amount) <= 0) { toast.error("Nominal harus lebih dari nol."); return; }
    setLoading(true);
    const payload = { ...f, amount: Number(f.amount), category: f.category.trim() || "Lainnya" };
    try {
      if (isEdit) await api.patch(`/operational/entries/${entry.id}`, payload);
      else await api.post("/operational/entries", payload);
      toast.success(isEdit ? "Entri diperbarui." : "Entri ditambahkan.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="op-entry-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Ubah Entri" : "Tambah Entri Operasional"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="op-field-date" /></div>
            <div>
              <Label>Jenis</Label>
              <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
                <SelectTrigger data-testid="op-field-direction"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="in">Pemasukan</SelectItem><SelectItem value="out">Pengeluaran</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
            <Label>Kategori</Label>
            <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Listrik, Gaji, Perbaikan..." list="op-category-list" data-testid="op-field-category" />
            <datalist id="op-category-list">
              {categories.filter((c) => c.direction === f.direction || c.direction === "both").map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </div>
            <div><Label>Nominal (Rp)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="op-field-amount" /></div>
          </div>
          <div><Label>Keterangan</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="op-field-description" /></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
                <SelectTrigger data-testid="op-field-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Tunai</SelectItem><SelectItem value="transfer">Transfer</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Referensi (opsional)</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="op-field-reference" /></div>
          </div>
          <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="op-field-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="op-save-button">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CAT_DIR_LABEL = { in: "Pemasukan", out: "Pengeluaran", both: "Keduanya" };

function CategoryDialog({ categories, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [direction, setDirection] = useState("both");
  const [loading, setLoading] = useState(false);
  const add = async () => {
    if (!name.trim()) { toast.error("Nama kategori wajib diisi."); return; }
    setLoading(true);
    try {
      await api.post("/operational/categories", { name: name.trim(), direction });
      setName(""); toast.success("Kategori ditambahkan."); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const del = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"?`)) return;
    try { await api.delete(`/operational/categories/${c.id}`); toast.success("Kategori dihapus."); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="op-category-dialog">
        <DialogHeader><DialogTitle>Kelola Kategori</DialogTitle></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label>Nama Kategori</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Listrik, Gaji..." data-testid="op-category-name" /></div>
          <div className="w-32">
            <Label>Jenis</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="op-category-direction"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="both">Keduanya</SelectItem><SelectItem value="in">Pemasukan</SelectItem><SelectItem value="out">Pengeluaran</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={loading} data-testid="op-category-add"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
          {categories.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Belum ada kategori. Kategori yang Anda pakai di entri juga tersimpan otomatis.</p>
          ) : categories.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm" data-testid={`op-category-row-${c.id}`}>
              <span>{c.name} <span className="text-xs text-muted-foreground">· {CAT_DIR_LABEL[c.direction] || c.direction}</span></span>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => del(c)} data-testid={`op-category-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BudgetDialog({ budgets, categories, onClose, onChanged }) {
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!category.trim()) { toast.error("Kategori wajib diisi."); return; }
    if (!Number(amount) || Number(amount) <= 0) { toast.error("Batas anggaran harus lebih dari nol."); return; }
    setLoading(true);
    try {
      await api.post("/operational/budgets", { category: category.trim(), amount: Number(amount) });
      setCategory(""); setAmount(""); toast.success("Anggaran disimpan."); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const del = async (b) => {
    if (!window.confirm(`Hapus anggaran "${b.category}"?`)) return;
    try { await api.delete(`/operational/budgets/${b.id}`); toast.success("Anggaran dihapus."); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="op-budget-dialog">
        <DialogHeader><DialogTitle>Atur Anggaran Bulanan</DialogTitle></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label>Kategori</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Listrik, Gaji..." list="op-budget-cat-list" data-testid="op-budget-category" />
            <datalist id="op-budget-cat-list">
              {categories.filter((c) => c.direction === "out" || c.direction === "both").map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </div>
          <div className="w-36"><Label>Batas (Rp)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="op-budget-amount" /></div>
          <Button onClick={save} disabled={loading} data-testid="op-budget-add"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="mt-3 max-h-72 space-y-1 overflow-y-auto">
          {budgets.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Belum ada anggaran. Tetapkan batas pengeluaran bulanan per kategori.</p>
          ) : budgets.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm" data-testid={`op-budget-row-${b.id}`}>
              <span>{b.category} <span className="text-xs text-muted-foreground">· batas <Money value={b.limit} /></span></span>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => del(b)} data-testid={`op-budget-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
